import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Link,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useLanguage } from '../App';

// Property translations
const propertyTranslations = {
  en: {
    properties_title: 'Properties',
    new_property: 'New Property',
    property_name: 'Property Name',
    owner: 'Owner',
    links: 'Links',
    lock_info: 'Lock Info',
    times: 'Check Times',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    no_properties: 'No properties found',
    add_property: 'Add Property',
    edit_property: 'Edit Property',
    property_nickname: 'Property Nickname',
    owner_name: 'Owner Name',
    airbnb_link: 'Airbnb Link',
    booking_link: 'Booking.com Link',
    vrbo_link: 'VRBO Link',
    other_links: 'Other Links',
    check_in_time: 'Check-in Time',
    check_out_time: 'Check-out Time',
    check_in_buffer: 'Check-in Buffer (minutes)',
    check_out_buffer: 'Check-out Buffer (minutes)',
    lock_id: 'Lock ID',
    lock_id_placeholder: '8-digit lock identifier',
    time_format_help: '24-hour format (HH:MM)',
    buffer_help: 'Minutes before/after for access code activation',
    smart_lock_settings: 'Smart Lock Settings',
    access_times: 'Access Times',
    save: 'Save',
    cancel: 'Cancel',
    delete_confirmation: 'Delete Property',
    delete_message: 'Are you sure you want to delete this property?',
    property_saved: 'Property saved successfully!',
    property_deleted: 'Property deleted successfully!',
    error_saving: 'Error saving property',
    error_deleting: 'Error deleting property',
    error_loading: 'Error loading properties',
    validation_required: 'This field is required',
    validation_lock_id: 'Lock ID must be exactly 8 characters',
    validation_time_format: 'Time must be in HH:MM format (24-hour)'
  },
  pt: {
    properties_title: 'Propriedades',
    new_property: 'Nova Propriedade',
    property_name: 'Nome da Propriedade',
    owner: 'Proprietário',
    links: 'Links',
    lock_info: 'Info da Fechadura',
    times: 'Horários',
    actions: 'Ações',
    edit: 'Editar',
    delete: 'Excluir',
    no_properties: 'Nenhuma propriedade encontrada',
    add_property: 'Adicionar Propriedade',
    edit_property: 'Editar Propriedade',
    property_nickname: 'Apelido da Propriedade',
    owner_name: 'Nome do Proprietário',
    airbnb_link: 'Link do Airbnb',
    booking_link: 'Link do Booking.com',
    vrbo_link: 'Link do VRBO',
    other_links: 'Outros Links',
    check_in_time: 'Horário de Check-in',
    check_out_time: 'Horário de Check-out',
    check_in_buffer: 'Buffer Check-in (minutos)',
    check_out_buffer: 'Buffer Check-out (minutos)',
    lock_id: 'ID da Fechadura',
    lock_id_placeholder: 'Identificador de 8 dígitos',
    time_format_help: 'Formato 24 horas (HH:MM)',
    buffer_help: 'Minutos antes/depois para ativação do código',
    smart_lock_settings: 'Configurações da Fechadura Inteligente',
    access_times: 'Horários de Acesso',
    save: 'Salvar',
    cancel: 'Cancelar',
    delete_confirmation: 'Excluir Propriedade',
    delete_message: 'Tem certeza que deseja excluir esta propriedade?',
    property_saved: 'Propriedade salva com sucesso!',
    property_deleted: 'Propriedade excluída com sucesso!',
    error_saving: 'Erro ao salvar propriedade',
    error_deleting: 'Erro ao excluir propriedade',
    error_loading: 'Erro ao carregar propriedades',
    validation_required: 'Este campo é obrigatório',
    validation_lock_id: 'ID da fechadura deve ter exatamente 8 caracteres',
    validation_time_format: 'Horário deve estar no formato HH:MM (24 horas)'
  },
  es: {
    properties_title: 'Propiedades',
    new_property: 'Nueva Propiedad',
    property_name: 'Nombre de la Propiedad',
    owner: 'Propietario',
    links: 'Enlaces',
    lock_info: 'Info de Cerradura',
    times: 'Horarios',
    actions: 'Acciones',
    edit: 'Editar',
    delete: 'Eliminar',
    no_properties: 'No se encontraron propiedades',
    add_property: 'Agregar Propiedad',
    edit_property: 'Editar Propiedad',
    property_nickname: 'Apodo de la Propiedad',
    owner_name: 'Nombre del Propietario',
    airbnb_link: 'Enlace de Airbnb',
    booking_link: 'Enlace de Booking.com',
    vrbo_link: 'Enlace de VRBO',
    other_links: 'Otros Enlaces',
    check_in_time: 'Hora de Check-in',
    check_out_time: 'Hora de Check-out',
    check_in_buffer: 'Buffer Check-in (minutos)',
    check_out_buffer: 'Buffer Check-out (minutos)',
    lock_id: 'ID de Cerradura',
    lock_id_placeholder: 'Identificador de 8 dígitos',
    time_format_help: 'Formato 24 horas (HH:MM)',
    buffer_help: 'Minutos antes/después para activación del código',
    smart_lock_settings: 'Configuración de Cerradura Inteligente',
    access_times: 'Horarios de Acceso',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete_confirmation: 'Eliminar Propiedad',
    delete_message: '¿Está seguro que desea eliminar esta propiedad?',
    property_saved: '¡Propiedad guardada exitosamente!',
    property_deleted: '¡Propiedad eliminada exitosamente!',
    error_saving: 'Error al guardar propiedad',
    error_deleting: 'Error al eliminar propiedad',
    error_loading: 'Error al cargar propiedades',
    validation_required: 'Este campo es obligatorio',
    validation_lock_id: 'ID de cerradura debe tener exactamente 8 caracteres',
    validation_time_format: 'Hora debe estar en formato HH:MM (24 horas)'
  }
};

function PropertyList() {
  const [properties, setProperties] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, property: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, property: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { language } = useLanguage();

  const t = (key) => {
    return propertyTranslations[language]?.[key] || propertyTranslations.en[key] || key;
  };

  // Mock data for now - replace with actual API calls
  useEffect(() => {
    loadPropertiesAndSites();
  }, []);

  const loadPropertiesAndSites = async () => {
    try {
      setLoading(true);
      
      // Load sites from localStorage
      const savedSites = localStorage.getItem('sites');
      if (savedSites) {
        setSites(JSON.parse(savedSites));
      }
      
      // Load properties from localStorage
      const savedProperties = localStorage.getItem('properties');
      if (savedProperties) {
        setProperties(JSON.parse(savedProperties));
      } else {
        // Mock data - replace with actual API call
        const mockProperties = [
          {
            id: '1',
            name: 'NR305',
            owner: 'Allan Homenhuck',
            checkInTime: '15:00',
            checkOutTime: '11:00',
            checkInBuffer: '30',
            checkOutBuffer: '60',
            lockId: 'LK001305',
            links: {
              airbnb: 'https://airbnb.com/rooms/12345',
              bookingcom: 'https://booking.com/hotel/br/property.html'
            }
          },
          {
            id: '2',
            name: 'Beach House',
            owner: 'Maria Silva',
            checkInTime: '16:00',
            checkOutTime: '10:00',
            checkInBuffer: '45',
            checkOutBuffer: '90',
            lockId: 'BH002456',
            links: {
              airbnb: 'https://airbnb.com/rooms/67890',
              vrbo: 'https://vrbo.com/property/123',
              other: 'https://example.com/property'
            }
          }
        ];
        setProperties(mockProperties);
        localStorage.setItem('properties', JSON.stringify(mockProperties));
      }
      
      setTimeout(() => {
        setLoading(false);
      }, 500);
    } catch (error) {
      setSnackbar({ open: true, message: t('error_loading'), severity: 'error' });
      setLoading(false);
    }
  };

  const handleSave = async (propertyData) => {
    try {
      let updatedProperties;
      
      if (editDialog.property) {
        // Update existing property
        updatedProperties = properties.map(p => 
          p.id === editDialog.property.id ? { ...propertyData, id: editDialog.property.id } : p
        );
      } else {
        // Add new property
        const newProperty = { ...propertyData, id: Date.now().toString() };
        updatedProperties = [...properties, newProperty];
      }
      
      // Save to localStorage
      localStorage.setItem('properties', JSON.stringify(updatedProperties));
      setProperties(updatedProperties);
      
      setEditDialog({ open: false, property: null });
      setSnackbar({ open: true, message: t('property_saved'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('error_saving'), severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      const updatedProperties = properties.filter(p => p.id !== deleteDialog.property.id);
      
      // Save to localStorage
      localStorage.setItem('properties', JSON.stringify(updatedProperties));
      setProperties(updatedProperties);
      
      setDeleteDialog({ open: false, property: null });
      setSnackbar({ open: true, message: t('property_deleted'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('error_deleting'), severity: 'error' });
    }
  };

  const renderLinks = (property) => {
    const links = [];
    
    // Handle new structure with links object
    if (property.links) {
      Object.entries(property.links).forEach(([key, url]) => {
        if (url && url.trim()) {
          // Find the site name from the key
          const site = sites.find(s => s.name.toLowerCase().replace(/\s+/g, '').replace('.', '') === key);
          const siteName = site ? site.name : key.charAt(0).toUpperCase() + key.slice(1);
          const siteColor = site ? site.color : '#666666';
          
          links.push({ name: siteName, url: url, color: siteColor });
        }
      });
    }
    
    // Handle old structure for backward compatibility
    if (property.airbnbLink) links.push({ name: 'Airbnb', url: property.airbnbLink, color: '#FF5A5F' });
    if (property.bookingLink) links.push({ name: 'Booking', url: property.bookingLink, color: '#003580' });
    if (property.vrboLink) links.push({ name: 'VRBO', url: property.vrboLink, color: '#0066CC' });
    if (property.otherLinks) links.push({ name: 'Other', url: property.otherLinks, color: '#666666' });

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {links.map((link, index) => (
          <Chip
            key={index}
            label={link.name}
            component={Link}
            href={link.url}
            target="_blank"
            clickable
            size="small"
            icon={<LinkIcon />}
            sx={{
              backgroundColor: link.color || '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: link.color || '#1565c0',
                opacity: 0.8
              }
            }}
          />
        ))}
        {links.length === 0 && <Typography variant="body2" color="textSecondary">-</Typography>}
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('properties_title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditDialog({ open: true, property: null })}
        >
          {t('new_property')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('property_name')}</TableCell>
              <TableCell>{t('owner')}</TableCell>
              <TableCell>{t('lock_info')}</TableCell>
              <TableCell>{t('times')}</TableCell>
              <TableCell>{t('links')}</TableCell>
              <TableCell align="center">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {properties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">
                    {t('no_properties')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              properties.map((property) => (
                <TableRow key={property.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HomeIcon color="primary" />
                      <Typography variant="body1" fontWeight="bold">
                        {property.name || property.nickname}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{property.owner}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon color="secondary" fontSize="small" />
                      <Typography variant="body2" fontFamily="monospace">
                        {property.lockId || '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="success" fontSize="small" />
                        <Typography variant="body2">
                          In: {property.checkInTime || '-'}
                          {property.checkInBuffer && ` (±${property.checkInBuffer}m)`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="error" fontSize="small" />
                        <Typography variant="body2">
                          Out: {property.checkOutTime || '-'}
                          {property.checkOutBuffer && ` (±${property.checkOutBuffer}m)`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{renderLinks(property)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => setEditDialog({ open: true, property })}
                      title={t('edit')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, property })}
                      title={t('delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Property Dialog */}
      <PropertyDialog
        open={editDialog.open}
        property={editDialog.property}
        sites={sites}
        onClose={() => setEditDialog({ open: false, property: null })}
        onSave={handleSave}
        t={t}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, property: null })}
      >
        <DialogTitle>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('delete_message')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>{deleteDialog.property?.nickname}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, property: null })}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Property Edit/Add Dialog Component
function PropertyDialog({ open, property, sites, onClose, onSave, t }) {
  const [formData, setFormData] = useState({
    name: '',
    owner: '',
    checkInTime: '',
    checkOutTime: '',
    checkInBuffer: '',
    checkOutBuffer: '',
    lockId: '',
    links: {}
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (property) {
      // Handle both old and new structure
      let links = {};
      
      if (property.links) {
        // New structure
        links = { ...property.links };
      } else {
        // Convert old structure to new
        if (property.airbnbLink) links.airbnb = property.airbnbLink;
        if (property.bookingLink) links.bookingcom = property.bookingLink;
        if (property.vrboLink) links.vrbo = property.vrboLink;
        if (property.otherLinks) links.other = property.otherLinks;
      }
      
      setFormData({
        name: property.name || property.nickname || '',
        owner: property.owner || '',
        checkInTime: property.checkInTime || '',
        checkOutTime: property.checkOutTime || '',
        checkInBuffer: property.checkInBuffer || '',
        checkOutBuffer: property.checkOutBuffer || '',
        lockId: property.lockId || '',
        links: links
      });
    } else {
      // Initialize with empty links for all available sites
      const emptyLinks = {};
      sites.forEach(site => {
        const key = site.name.toLowerCase().replace(/\s+/g, '').replace('.', '');
        emptyLinks[key] = '';
      });
      
      setFormData({
        name: '',
        owner: '',
        checkInTime: '',
        checkOutTime: '',
        checkInBuffer: '',
        checkOutBuffer: '',
        lockId: '',
        links: emptyLinks
      });
    }
    setErrors({});
  }, [property, sites]);

  const handleLinkChange = (siteKey, value) => {
    setFormData({
      ...formData,
      links: {
        ...formData.links,
        [siteKey]: value
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = t('validation_required');
    }
    if (!formData.owner.trim()) {
      newErrors.owner = t('validation_required');
    }
    if (!formData.checkInTime.trim()) {
      newErrors.checkInTime = t('validation_required');
    }
    if (!formData.checkOutTime.trim()) {
      newErrors.checkOutTime = t('validation_required');
    }
    if (!formData.lockId.trim()) {
      newErrors.lockId = t('validation_required');
    }

    // Time format validation (HH:MM)
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (formData.checkInTime && !timeRegex.test(formData.checkInTime)) {
      newErrors.checkInTime = t('validation_time_format');
    }
    if (formData.checkOutTime && !timeRegex.test(formData.checkOutTime)) {
      newErrors.checkOutTime = t('validation_time_format');
    }

    // Lock ID validation (exactly 8 characters)
    if (formData.lockId && formData.lockId.length !== 8) {
      newErrors.lockId = t('validation_lock_id');
    }

    // Buffer validation (positive numbers)
    if (formData.checkInBuffer && (isNaN(formData.checkInBuffer) || parseInt(formData.checkInBuffer) < 0)) {
      newErrors.checkInBuffer = 'Must be a positive number';
    }
    if (formData.checkOutBuffer && (isNaN(formData.checkOutBuffer) || parseInt(formData.checkOutBuffer) < 0)) {
      newErrors.checkOutBuffer = 'Must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {property ? t('edit_property') : t('add_property')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('property_name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('owner_name')}
              value={formData.owner}
              onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
              required
              error={!!errors.owner}
              helperText={errors.owner}
            />
          </Grid>

          {/* Smart Lock Settings */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom color="secondary">
              <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('smart_lock_settings')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('lock_id')}
              value={formData.lockId}
              onChange={(e) => setFormData({ ...formData, lockId: e.target.value.toUpperCase() })}
              required
              placeholder={t('lock_id_placeholder')}
              inputProps={{ maxLength: 8 }}
              error={!!errors.lockId}
              helperText={errors.lockId || 'Exactly 8 characters (letters/numbers)'}
            />
          </Grid>

          {/* Access Times */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('access_times')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label={t('check_in_time')}
              value={formData.checkInTime}
              onChange={(e) => setFormData({ ...formData, checkInTime: e.target.value })}
              required
              placeholder="15:00"
              helperText={errors.checkInTime || t('time_format_help')}
              error={!!errors.checkInTime}
              inputProps={{ pattern: '[0-2][0-9]:[0-5][0-9]' }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label={t('check_in_buffer')}
              value={formData.checkInBuffer}
              onChange={(e) => setFormData({ ...formData, checkInBuffer: e.target.value })}
              type="number"
              placeholder="30"
              helperText={errors.checkInBuffer || t('buffer_help')}
              error={!!errors.checkInBuffer}
              inputProps={{ min: 0, max: 1440 }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label={t('check_out_time')}
              value={formData.checkOutTime}
              onChange={(e) => setFormData({ ...formData, checkOutTime: e.target.value })}
              required
              placeholder="11:00"
              helperText={errors.checkOutTime || t('time_format_help')}
              error={!!errors.checkOutTime}
              inputProps={{ pattern: '[0-2][0-9]:[0-5][0-9]' }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label={t('check_out_buffer')}
              value={formData.checkOutBuffer}
              onChange={(e) => setFormData({ ...formData, checkOutBuffer: e.target.value })}
              type="number"
              placeholder="60"
              helperText={errors.checkOutBuffer || t('buffer_help')}
              error={!!errors.checkOutBuffer}
              inputProps={{ min: 0, max: 1440 }}
            />
          </Grid>

          {/* Booking Platform Links */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Booking Platform Links
            </Typography>
          </Grid>
          
          {/* Dynamic link fields based on available sites */}
          {sites.map((site) => {
            const siteKey = site.name.toLowerCase().replace(/\s+/g, '').replace('.', '');
            return (
              <Grid item xs={12} key={site.id}>
                <TextField
                  fullWidth
                  label={`${site.name} Link`}
                  value={formData.links[siteKey] || ''}
                  onChange={(e) => handleLinkChange(siteKey, e.target.value)}
                  placeholder={`https://${site.url.replace('https://', '')}/property/...`}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: site.color,
                          mr: 1
                        }}
                      />
                    )
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!formData.name || !formData.owner || !formData.checkInTime || !formData.checkOutTime || !formData.lockId}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PropertyList;
